import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPublicProducts } from '../../services/products';
import { PRODUCT_CATEGORIES, STORAGE_KEYS, MAX_SAMPLE_ITEMS, SAMPLE_ELIGIBLE_UNITS } from '../../utils/constants';
import GrainDropdown from '../../components/products/GrainDropdown';
import './Catalog.css';

const MODAL_DELAY_MS = 30_000; // 30 seconds
const MODAL_DISMISSED_KEY = 'catalog_cta_dismissed';

const CATEGORY_ORDER = PRODUCT_CATEGORIES.map((c) => c.key);

const Catalog = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeGrain, setActiveGrain] = useState('');
  const [openSampleProductId, setOpenSampleProductId] = useState(null);

  // ── Sample cart ───────────────────────────────────────────────────────────
  const [sampleCart, setSampleCart] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SAMPLE_CART);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persist sample cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.SAMPLE_CART, JSON.stringify(sampleCart));
    } catch {
      // ignore
    }
  }, [sampleCart]);

  const totalSampleCount = sampleCart.length;

  const addSampleItem = useCallback((size, productName) => {
    setSampleCart(prev => {
      // Enforce max by distinct variants, consistent with the authenticated cart flow
      if (prev.length >= MAX_SAMPLE_ITEMS) return prev;
      if (prev.some(i => i.productVariantId === size.id)) return prev;
      return [...prev, {
        productVariantId: size.id,
        productName,
        variantName: size.variant_name,
        quantity: 1,
      }];
    });
  }, []);

  const removeSampleItem = useCallback((variantId) => {
    setSampleCart(prev => prev.filter(i => i.productVariantId !== variantId));
  }, []);

  const handleRequestSamples = useCallback(() => {
    // Sample cart is already persisted to localStorage — just navigate
    navigate('/signup');
  }, [navigate]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const data = await getPublicProducts();
        // Support both paginated and flat responses
        setProducts(Array.isArray(data) ? data : (data.results || []));
      } catch {
        setError('Unable to load the product catalog. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Timed modal — fires once per session
  useEffect(() => {
    if (sessionStorage.getItem(MODAL_DISMISSED_KEY)) return;
    const timer = setTimeout(() => setShowModal(true), MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  const dismissModal = useCallback(() => {
    setShowModal(false);
    sessionStorage.setItem(MODAL_DISMISSED_KEY, '1');
  }, []);

  // Escape key + focus trap for the modal
  useEffect(() => {
    if (!showModal) return;

    // Store previously focused element so we can restore it on close
    previousFocusRef.current = document.activeElement;

    // Focus the first focusable element in the modal
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusable = modalRef.current?.querySelectorAll(focusableSelector);
    if (focusable?.length) focusable[0].focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        dismissModal();
        return;
      }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const elements = modalRef.current.querySelectorAll(focusableSelector);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [showModal, dismissModal]);

  return (
    <div className="catalog">

      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="catalog__header">
        <div className="catalog__header-video-wrap">
          <iframe
            src="https://www.youtube.com/embed/UB43h0p63YM?autoplay=1&mute=1&loop=1&playlist=UB43h0p63YM&controls=0&rel=0&playsinline=1&showinfo=0&modestbranding=1"
            title="Deep Roots Milling background"
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
          />
        </div>
        <div className="catalog__header-inner">
          <p className="catalog__wordmark">Deep Roots Milling</p>
          <div className="catalog__header-divider">
            <span className="catalog__divider-line" />
            <span className="catalog__divider-ornament">❧</span>
            <span className="catalog__divider-line" />
          </div>
          <h1 className="catalog__title">Wholesale Catalog</h1>
          <p className="catalog__subtitle">
            Stone-milled grains and flours, grown with care and milled to order.
          </p>
          <div className="catalog__cta-group">
            <Link to="/signup" className="catalog__cta-primary">
              Request Wholesale Access
            </Link>
            <Link to="/login" className="catalog__cta-secondary">
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* ── Loading ────────────────────────────────────────────── */}
      {loading && (
        <div className="catalog__loading">
          <div className="catalog__loading-spinner" />
          <p>Loading catalog…</p>
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────── */}
      {error && (
        <div className="catalog__error">
          <p>{error}</p>
        </div>
      )}

      {/* ── Filters ────────────────────────────────────────────── */}
      {!loading && !error && products.length > 0 && (() => {
        const presentKeys = new Set(products.flatMap((p) => (p.categories || []).map((c) => c.key)));
        const availableCategories = PRODUCT_CATEGORIES.filter((c) => presentKeys.has(c.key));

        // Collect unique grain varieties across all products, sorted by name
        const grainMap = new Map();
        products.forEach((p) => {
          (p.grain_varieties || []).forEach((g) => grainMap.set(g.id, g.name));
        });
        const grainOptions = [...grainMap.entries()]
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([id, name]) => ({ id, name }));

        if (availableCategories.length === 0 && grainOptions.length === 0) return null;

        return (
          <div className="catalog__filters-wrap">
            {availableCategories.length > 0 && (
              <nav className="catalog__filters" aria-label="Filter by category">
                <button
                  type="button"
                  className={`catalog__filter-btn ${!activeCategory ? 'catalog__filter-btn--active' : ''}`}
                  onClick={() => setActiveCategory(null)}
                >
                  All
                </button>
                {availableCategories.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    className={`catalog__filter-btn ${activeCategory === cat.key ? 'catalog__filter-btn--active' : ''}`}
                    onClick={() => setActiveCategory(cat.key)}
                  >
                    {cat.label}
                  </button>
                ))}
              </nav>
            )}
            {grainOptions.length > 0 && (
              <div className="catalog__grain-filter">
                <GrainDropdown
                  options={grainOptions}
                  value={activeGrain}
                  onChange={setActiveGrain}
                  classPrefix="catalog__grain"
                />
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Product list ───────────────────────────────────────── */}
      {!loading && !error && (() => {
        // Sort by category order, then name; products with no category go last
        const sorted = [...products].sort((a, b) => {
          const aKeys = (a.categories || []).map((c) => c.key);
          const bKeys = (b.categories || []).map((c) => c.key);
          const ai = aKeys.length > 0 ? Math.min(...aKeys.map((k) => CATEGORY_ORDER.indexOf(k)).filter((i) => i >= 0)) : Infinity;
          const bi = bKeys.length > 0 ? Math.min(...bKeys.map((k) => CATEGORY_ORDER.indexOf(k)).filter((i) => i >= 0)) : Infinity;
          if (ai !== bi) return ai - bi;
          return a.name.localeCompare(b.name);
        });

        const filtered = sorted.filter((p) => {
          const matchesCategory = !activeCategory || (p.categories || []).some((c) => c.key === activeCategory);
          const matchesGrain = !activeGrain || (p.grain_varieties || []).some((g) => String(g.id) === activeGrain);
          return matchesCategory && matchesGrain;
        });

        return (
          <section className="catalog__products">
            {filtered.length === 0 ? (
              <p className="catalog__empty">No products available at this time.</p>
            ) : (
              filtered.map((product, index) => {
                const imageLeft = index % 2 !== 0;
                const eligibleSizes = (product.sizes || []).filter(s => SAMPLE_ELIGIBLE_UNITS.has(s.unit));

                return (
                  <article
                    key={product.id}
                    className={`catalog__product ${imageLeft ? 'catalog__product--image-left' : 'catalog__product--image-right'}`}
                  >
                    {/* Image */}
                    <div className="catalog__product-image-wrap">
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="catalog__product-image"
                        />
                      ) : (
                        <div className="catalog__product-image-placeholder">
                          <span>No image</span>
                        </div>
                      )}
                    </div>

                    {/* Text */}
                    <div className="catalog__product-text">
                      {product.categories && product.categories.length > 0 && (
                        <span className="catalog__product-category">
                          {product.categories.map((c) => c.label).join(' · ')}
                        </span>
                      )}

                      <h2 className="catalog__product-name">{product.name}</h2>

                      {product.growers && product.growers.length > 0 && (
                        <div className="catalog__product-growers">
                          <span className="catalog__growers-label">Grown by</span>
                          <div className="catalog__growers-list">
                            {product.growers.map((g) => (
                              <span key={g.id} className="catalog__grower-item">
                                <span className="catalog__grower-name">{g.farm_name}</span>
                                {g.region && <span className="catalog__grower-region">{g.region}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {product.grain_varieties && product.grain_varieties.length > 0 && (
                        <div className="catalog__product-varieties">
                          {product.grain_varieties.map((v) => (
                            <span key={v.id} className="catalog__variety-tag">
                              {v.name}
                            </span>
                          ))}
                        </div>
                      )}

                      {product.description && (
                        <p className="catalog__product-description">{product.description}</p>
                      )}

                      {product.sizes && product.sizes.length > 0 && (
                        <div className="catalog__product-sizes">
                          <span className="catalog__sizes-label">Available sizes</span>
                          <div className="catalog__sizes-list">
                            {product.sizes.map((s) => (
                              <span key={s.id} className="catalog__size-pill">
                                {s.variant_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* ── Sample request section ────────────────────── */}
                      {eligibleSizes.length > 0 && (() => {
                        const isOpen = openSampleProductId === product.id;
                        const selectedForProduct = sampleCart.filter(i =>
                          eligibleSizes.some(s => s.id === i.productVariantId)
                        );
                        const hasSelection = selectedForProduct.length > 0;

                        return (
                          <div className="catalog__product-samples">
                            {/* CTA toggle */}
                            <button
                              type="button"
                              className={`catalog__sample-cta${hasSelection ? ' catalog__sample-cta--active' : ''}`}
                              onClick={() => setOpenSampleProductId(isOpen ? null : product.id)}
                              aria-expanded={isOpen}
                            >
                              <span>
                                {hasSelection
                                  ? `Sample requested — ${selectedForProduct.map(i => i.variantName).join(', ')}`
                                  : 'Request a Sample'}
                              </span>
                              <span className="catalog__sample-cta-chevron" aria-hidden="true">
                                {isOpen ? '↑' : '↓'}
                              </span>
                            </button>

                            {/* Expandable size picker */}
                            {isOpen && (
                              <div className="catalog__samples-list">
                                {eligibleSizes.map(size => {
                                  const selected = sampleCart.some(i => i.productVariantId === size.id);
                                  const atMax = totalSampleCount >= MAX_SAMPLE_ITEMS;

                                  return (
                                    <div key={size.id} className="catalog__sample-row">
                                      <span className="catalog__sample-size">{size.variant_name}</span>
                                      {selected ? (
                                        <button
                                          type="button"
                                          className="catalog__sample-add-btn catalog__sample-add-btn--selected"
                                          onClick={() => removeSampleItem(size.id)}
                                          aria-label={`Remove ${size.variant_name} sample`}
                                        >
                                          Selected ✓
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className={`catalog__sample-add-btn${atMax ? ' catalog__sample-add-btn--disabled' : ''}`}
                                          onClick={() => !atMax && addSampleItem(size, product.name)}
                                          disabled={atMax}
                                          title={atMax ? `Max ${MAX_SAMPLE_ITEMS} samples reached` : undefined}
                                        >
                                          {atMax ? 'Max reached' : 'Select'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </article>
                );
              })
            )}
          </section>
        );
      })()}

      {/* ── Footer CTA ─────────────────────────────────────────── */}
      {!loading && !error && products.length > 0 && (
        <footer className="catalog__footer">
          <div className="catalog__footer-inner">
            <h2 className="catalog__footer-heading">Ready to carry our products?</h2>
            <p className="catalog__footer-sub">
              Apply for a wholesale account and get access to pricing, ordering, and real-time
              fulfillment tracking.
            </p>
            <Link to="/signup" className="catalog__cta-primary">
              Request Wholesale Access
            </Link>
          </div>
        </footer>
      )}

      {/* ── Timed sign-up modal ────────────────────────────────── */}
      {showModal && (
        <div
          className="catalog__modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) dismissModal(); }}
        >
          <div
            ref={modalRef}
            className="catalog__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="catalog-modal-title"
          >
            <button
              type="button"
              className="catalog__modal-close"
              onClick={dismissModal}
              aria-label="Close"
            >
              ✕
            </button>

            <p className="catalog__modal-wordmark">Deep Roots Milling</p>
            <div className="catalog__modal-divider">
              <span className="catalog__modal-divider-line" />
              <span className="catalog__modal-divider-ornament">❧</span>
              <span className="catalog__modal-divider-line" />
            </div>

            <h2 id="catalog-modal-title" className="catalog__modal-title">
              Interested in Wholesale?
            </h2>
            <p className="catalog__modal-body">
              Apply for a wholesale account to access pricing, place orders, and
              track fulfillment direct from our mill.
            </p>

            <div className="catalog__modal-actions">
              <Link
                to="/signup"
                className="catalog__cta-primary"
                onClick={dismissModal}
              >
                Request Wholesale Access
              </Link>
              <button
                type="button"
                className="catalog__modal-dismiss"
                onClick={dismissModal}
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Floating sample cart bar ────────────────────────────── */}
      {sampleCart.length > 0 && (
        <div className="catalog__sample-bar" role="region" aria-label="Sample request cart">
          <div className="catalog__sample-bar-inner">
            <div className="catalog__sample-bar-info">
              <span className="catalog__sample-bar-count">
                {totalSampleCount} sample{totalSampleCount !== 1 ? 's' : ''} selected
              </span>
              <span className="catalog__sample-bar-items">
                {sampleCart.map(i =>
                  `${i.productName} ${i.variantName}${i.quantity > 1 ? ` ×${i.quantity}` : ''}`
                ).join(' · ')}
              </span>
            </div>
            <button
              type="button"
              className="catalog__sample-bar-btn"
              onClick={handleRequestSamples}
            >
              Request Wholesale Access →
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default Catalog;
